import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageByUrl, RetrievePageResult } from "@insite/client-framework/Services/ContentService";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import BlogCard, { BlogCardStyles } from "@tggstore/Components/BlogCard";
import { CustomState } from "@tggstore/Store/Reducers";
import { PostsStateType } from "@tggstore/Store/State/Blog/BlogListState";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",

    displayTitle = "Title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (customState: CustomState) => {
    return {
        isLoading: customState.blogList.isLoading,
        posts: customState.blogList.posts || [],
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface BlogLandingListWidgetStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noPostsText?: TypographyPresentationProps;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    gridContainerWrapper?: InjectableCss;
    gridContainerPosts?: GridContainerProps;
    gridItemPost?: GridItemProps;
    blogCardExtendedStyle?: BlogCardStyles;
}

export const blogLandingListWidgetStyles: BlogLandingListWidgetStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    titleWrapper: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    title: { variant: "h2" },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noPostsText: {
        size: 24,
        weight: "bold",
        css: css`
            text-align: center;
        `,
    },
    gridContainerPosts: { gap: 20 },
    gridItemPost: { width: [12, 6, 4, 3, 3] },
};

const styles = blogLandingListWidgetStyles;


interface PublishDate {
    id: string;
    publishDate: any;
}

const BlogLandingListWidget = ({ isLoading, posts, fields: { title } }: Props) => {

    const [reorderedPosts, setReorderedPosts] = useState<PostsStateType[]>([]);
    
        useEffect(() => {
            if (posts.length > 0) {
                handleLoadContent();
            }
        }, [posts]);
    
        const handleLoadContent = () => {
            if (posts.length > 0) {
                const tempPublishDates = [] as PublishDate[]; // Temporary array to store results
    
                const promises = posts.map(post =>
                    getPageByUrl(post.url).then((result: RetrievePageResult) => {
                        const { page } = result;
                        if (page) {
                            const { publishDate: widgetPublishDate } = page.generalFields;
                            tempPublishDates.push({
                                id: post.id,
                                publishDate: widgetPublishDate,
                            });
                        }
                    }),
                );
    
                Promise.all(promises).then(() => {
                    tempPublishDates.sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
                    const reordered = tempPublishDates
                        .map(dateItem => posts.find(post => post.id === dateItem.id))
                        .filter((post): post is PostsStateType => post !== undefined);
                    setReorderedPosts(reordered);
                });
            }
        };


    if (isLoading && !posts.length) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (posts.length === 0) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <Typography {...styles.noPostsText}>{translate("We're sorry, there were no posts found.")}</Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <LoadingOverlay loading={isLoading}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography as="h2" variant="body" {...styles.title}>
                        {title}
                    </Typography>
                </StyledWrapper>

                <StyledWrapper {...styles.gridContainerWrapper}>
                    <GridContainer {...styles.gridContainerPosts}>
                        {(reorderedPosts.length > 0 ? reorderedPosts : posts).map(post => ( 
                            <GridItem {...styles.gridItemPost} key={post.id}>
                                <BlogCard
                                    blogLink={
                                        {
                                            id: post.id,
                                            parentId: post.parentId,
                                            url: post.url,
                                        } as any
                                    }
                                    openNewWindow={false}
                                    extendedStyles={styles.blogCardExtendedStyle}
                                />
                            </GridItem>
                        ))}
                    </GridContainer>
                </StyledWrapper>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BlogLandingListWidget) as any,
    definition: {
        displayName: "Landing List",
        group: "Verndale Box - Blog" as any,
        icon: "blog",
        allowedContexts: ["BlogLandingPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: fields.displayTitle,
                editorTemplate: "TextField",
                defaultValue: "Recent Posts",
                fieldType: "Translatable",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
